import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	type ProjectType,
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants';

import { CoordinationClient } from '@atlassian/jira-engagement';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src';
import type { Environment } from '@atlassian/jira-shared-types';
import { useEnvironment, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	getModalDismissedProperty,
	getModalViewedProperty,
	setModalDismissedProperty,
	setModalViewedProperty,
} from './user-properties';

export type ShouldShowSpotlight = {
	shouldShow: boolean;
	onClose: () => void;
	onModalViewed: () => void;
	onModalDismissed: () => void;
};

const getEPMessageId = (environment: Environment, projectType: ProjectType) => {
	const isProd = environment === 'prod';
	let epMessageId;

	if (projectType === SOFTWARE_PROJECT) {
		epMessageId = isProd
			? 'jsw-upgrade-onboarding-spotlight'
			: 'jsw-upgrade-onboarding-spotlight-stg';
	} else if (projectType === SERVICE_DESK_PROJECT) {
		epMessageId = isProd
			? 'jsm-upgrade-onboarding-spotlight'
			: 'jsm-upgrade-onboarding-spotlight-stg';
	} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		epMessageId = isProd
			? 'jpd-upgrade-onboarding-spotlight'
			: 'jpd-upgrade-onboarding-spotlight-stg';
	}

	return epMessageId;
};

// We want to show the spotlight if the modal has been viewed but not dismissed
// if it was dismissed we showed the spotlight directly without EP
// if the modal was shown but not dismissed, we want to check EP
export const useShouldShowSpotlightOnLand = (projectType: ProjectType): ShouldShowSpotlight => {
	const environment = useEnvironment();
	const [shouldShow, setShouldShow] = useState<boolean>(false);

	const { cloudId, atlassianAccountId } = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: orgId } = useOrgId();

	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, '/gateway/api', false, orgId),
		[cloudId, orgId],
	);

	const epMessageId = getEPMessageId(environment, projectType);

	useEffect(() => {
		if (!atlassianAccountId || !epMessageId) return;

		Promise.all([
			getModalViewedProperty(createAnalyticsEvent, projectType, atlassianAccountId),
			getModalDismissedProperty(createAnalyticsEvent, projectType, atlassianAccountId),
		]).then(async ([wasModalViewed, wasModalDismissed]) => {
			// we only check EP if the modal was shown but not dismissed
			if (wasModalViewed && !wasModalDismissed) {
				const started = await coordinationClient.start(epMessageId);
				setShouldShow(started);
			}
		});
	}, [atlassianAccountId, coordinationClient, createAnalyticsEvent, epMessageId, projectType]);

	return {
		shouldShow,
		onModalViewed: useCallback(
			() =>
				atlassianAccountId &&
				setModalViewedProperty(createAnalyticsEvent, projectType, atlassianAccountId),
			[atlassianAccountId, createAnalyticsEvent, projectType],
		),
		onModalDismissed: useCallback(
			() =>
				atlassianAccountId &&
				setModalDismissedProperty(createAnalyticsEvent, projectType, atlassianAccountId),
			[atlassianAccountId, createAnalyticsEvent, projectType],
		),
		onClose: useCallback(() => {
			if (!epMessageId) return;

			setShouldShow(false);
			coordinationClient.stop(epMessageId);
		}, [coordinationClient, epMessageId]),
	};
};
